// PDFReportDocument.js
import { Fragment } from "react";
import { Document, Page, Text, View } from "@react-pdf/renderer";
import {
  PDF_PAGE_PADDING,
  CELL_PADDING_RIGHT,
  ROW_PADDING_VERTICAL,
  shouldKeepRowWhole,
} from "./pdfColumnWidths";
import { formatPdfFooter, formatPdfTotal } from "../reportExport";

const PDFReportDocument = ({
  title,
  headings,
  data,
  columnWidth,
  startDate,
  endDate,
  variableColumnWidthsArray,
  columnWidthTypeRadioValue,
  headingsAndDataFontSize,
  sumAndAvgFontSize,
  columnSums,
  columnAverages,
  showSUMRow,
  showAVGRow,
  headingsSpacing,
  dataSpacing,
  isDownload,
  /* JFCRX-7643. Set by the viewer in PREVIEW mode only: the preview is one
   * ten-row document per pager page, so react-pdf's counter below would
   * always read "1 / 1". When given, it replaces that counter (see
   * formatPdfFooter). The download passes nothing and keeps the real page
   * numbers. */
  footerLabel,
  /* JFCRX-7754. Preview only, alongside footerLabel: called with the number
   * of sheets this ten-row document rendered to, so the viewer can say when
   * the preview page continues below the visible sheet. */
  onSheetCount,
}) => {
  const ROWS_PER_PDF_PAGE = 40;

  const pages = [];

  /* The width of a data cell in column `i` for the selected width mode. The
   * Sum and Average cells use it too (JFCRX-7652), so a total sits under its
   * own column in every mode, not only in Variable Widths. */
  const dataCellWidth = (i) =>
    columnWidthTypeRadioValue === 1
      ? variableColumnWidthsArray[i]
      : columnWidthTypeRadioValue === 2
        ? columnWidth
        : dataSpacing[i].columnWidth;

  /* JFCRX-7652. Only totals that have a column are drawn, matching what the
   * width calculation measures. An entry past the last heading would be a
   * Text with no width, and flexShrink would squeeze the whole row off its
   * columns. */
  const totalsCells = (totals) =>
    totals.slice(0, headings.length).map((total, i) => (
      <Text
        key={i}
        style={{
          width: dataCellWidth(i),
          fontSize: sumAndAvgFontSize,
          paddingRight: CELL_PADDING_RIGHT,
        }}
      >
        {formatPdfTotal(total)}
      </Text>
    ));

  const headerView = (
    <View
      fixed
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
        fontSize: 10,
        fontWeight: 500,
        marginBottom: 10,
      }}
    >
      <Text>{title}</Text>
      <Text>{startDate && endDate ? `${startDate} - ${endDate}` : ""}</Text>
    </View>
  );

  const headingsRow = (
    <View
      fixed
      style={{
        flexDirection: "row",
        borderBottom: "2px solid #089BAB",
        paddingVertical: 12,
      }}
    >
      {headings.map((heading, i) => {
        let width =
          columnWidthTypeRadioValue === 1
            ? variableColumnWidthsArray[i]
            : columnWidthTypeRadioValue === 2
              ? columnWidth
              : headingsSpacing[i].columnWidth;

        return (
          <Text
            key={i}
            style={{
              width,
              fontSize: headingsAndDataFontSize,
              fontWeight: 500,
              paddingRight: CELL_PADDING_RIGHT,
            }}
          >
            {heading}
          </Text>
        );
      })}
    </View>
  );

  /* JFCRX-7643. At least one page, so a report with no rows still shows its
   * header, headings and footer rather than a blank viewer. */
  const pageCount = Math.max(1, Math.ceil(data.length / ROWS_PER_PDF_PAGE));
  for (let p = 0; p < pageCount; p += 1) {
    const i = p * ROWS_PER_PDF_PAGE;
    const pageRows = data.slice(i, i + ROWS_PER_PDF_PAGE);
    const isLastPage = p === pageCount - 1;

    pages.push(
      <Page
        key={i}
        size="LETTER"
        orientation="landscape"
        style={{ padding: PDF_PAGE_PADDING, fontFamily: "Poppins" }}
      >
        {headerView}
        {headingsRow}
        {pageRows.map((row, rIdx) => {
          /* JFCRX-7755. Kept whole when it safely fits, so a page break never
           * scatters one record's cells over two pages; a row too tall for
           * that stays splittable and breaks by line, with orphans and widows
           * of 1 so no cell moves to the next page whole. A splittable row
           * draws each cell in a View of its column's width, so its columns
           * stay in place on the next page. See ROW HEIGHT and CELL VIEWS ON
           * SPLITTABLE ROWS in pdfColumnWidths.js. */
          const splittable = !shouldKeepRowWhole({
            cells: row,
            widths: row.map((_, cIdx) => dataCellWidth(cIdx)),
            fontSize: headingsAndDataFontSize,
          });
          return (
            <View
              key={rIdx}
              wrap={splittable}
              style={{
                flexDirection: "row",
                backgroundColor: rIdx % 2 ? "#EBEDF8" : "#FFFFFF",
                paddingVertical: ROW_PADDING_VERTICAL,
              }}
            >
              {row.map((cell, cIdx) => {
                const text = (
                  <Text
                    key={cIdx}
                    orphans={1}
                    widows={1}
                    style={{
                      width: dataCellWidth(cIdx),
                      fontSize: headingsAndDataFontSize,
                      paddingRight: CELL_PADDING_RIGHT,
                    }}
                  >
                    {cell}
                  </Text>
                );
                return splittable ? (
                  <View key={cIdx} style={{ width: dataCellWidth(cIdx) }}>
                    {text}
                  </View>
                ) : (
                  text
                );
              })}
            </View>
          );
        })}
        <Text
          fixed
          style={{
            position: "absolute",
            bottom: 6,
            width: "100%",
            textAlign: "center",
            fontSize: 10,
          }}
          render={({ pageNumber, totalPages }) => {
            /* JFCRX-7754. react-pdf calls this twice per page, and the first
             * pass has no totalPages yet, hence the guard. */
            if (footerLabel && onSheetCount && Number.isFinite(totalPages)) {
              onSheetCount(totalPages);
            }
            return formatPdfFooter({ footerLabel, pageNumber, totalPages });
          }}
        />

        {/* JFCRX-7755. Each label is kept on one page with its totals; the
            pair is a few lines at most, far under a page. */}
        {isDownload && isLastPage && (
          <Fragment>
            {showSUMRow && (
              <View wrap={false}>
                <Text
                  style={{
                    fontSize: sumAndAvgFontSize,
                    fontWeight: 500,
                    color: "#089BAB",
                    paddingTop: 12,
                  }}
                >
                  Sum
                </Text>
                <View style={{ flexDirection: "row", paddingTop: 10 }}>
                  {totalsCells(columnSums)}
                </View>
              </View>
            )}
            {showAVGRow && (
              <View wrap={false}>
                <Text
                  style={{
                    fontSize: sumAndAvgFontSize,
                    fontWeight: 500,
                    color: "#089BAB",
                    paddingTop: 12,
                  }}
                >
                  Average
                </Text>
                <View style={{ flexDirection: "row", paddingTop: 10 }}>
                  {totalsCells(columnAverages)}
                </View>
              </View>
            )}
          </Fragment>
        )}
      </Page>,
    );
  }

  return <Document title={title}>{pages}</Document>;
};

export default PDFReportDocument;
